import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
/**
 * The wishlist app renders one heart per product per page. When a card switches to a product that already has a
 * heart elsewhere on the page, show a mirror of that heart which forwards clicks to it.
 * @param {HTMLElement} card
 * @param {string} productId
 */
function ensureWishlistHeart(card, productId) {
  if (!card.isConnected || !productId) return;
  if (card.querySelector('.Vtl-WishlistIcon__Wrapper, .spikes-wishlist-proxy')) return;

  const findSource = () =>
    [...document.querySelectorAll(`[class*="Vtl-WishlistIcon__Wrapper--Id-${productId}-"]`)].find(
      (element) => !card.contains(element)
    );

  const source = findSource();
  if (!(source instanceof HTMLElement)) return;

  const sourceStyle = getComputedStyle(source);
  const proxy = document.createElement('div');
  proxy.className = 'spikes-wishlist-proxy';
  proxy.style.cssText = `position: absolute; top: ${source.style.top || sourceStyle.top}; left: ${
    source.style.left || sourceStyle.left
  }; z-index: ${sourceStyle.zIndex === 'auto' ? 2 : sourceStyle.zIndex};`;

  const sync = () => {
    const current = findSource();
    if (!current) {
      proxy.hidden = true;
      return;
    }
    proxy.hidden = false;
    if (proxy.innerHTML !== current.innerHTML) proxy.innerHTML = current.innerHTML;
  };
  sync();

  proxy.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const target = findSource()?.querySelector('[role="button"], button');
    if (target instanceof HTMLElement) target.click();
  });

  const observer = new MutationObserver((mutations) => {
    if (!proxy.isConnected) {
      observer.disconnect();
      return;
    }
    if (mutations.every((mutation) => proxy.contains(mutation.target))) return;
    sync();
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'aria-label', 'title'] });

  card.append(proxy);
}

export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    const link = this.refs.productCardLink;
    if (!link.getAttribute('href')) return '';

    const url = new URL(link.href);
    const variantOverride = this.#getProductPageVariantOverride();

    if (variantOverride?.action === 'set') {
      url.searchParams.set('variant', variantOverride.variantId);
    } else if (variantOverride?.action === 'remove') {
      url.searchParams.delete('variant');
    }

    return url.toString();
  }

  /**
   * Gets the user-selected variant ID that still needs to be synced from product-card state.
   * @returns {string | null} Variant ID or null.
   */
  getSelectedVariantId() {
    const variantOverride = this.#getProductPageVariantOverride();
    return variantOverride?.action === 'set' ? variantOverride.variantId : null;
  }

  /**
   * Gets the user-selected variant override for product-page URLs.
   * @returns {{ action: 'set', variantId: string } | { action: 'remove' } | null}
   */
  #getProductPageVariantOverride() {
    const link = this.refs.productCardLink;
    if (link.getAttribute('href')) {
      const url = new URL(link.href);
      if (url.searchParams.has('variant')) return null;
    }

    const variantId = this.#getCheckedVariantId();
    if (variantId === undefined) return null;

    return variantId ? { action: 'set', variantId } : { action: 'remove' };
  }

  /** @returns {string | undefined} */
  #getCheckedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#handleQuickAdd();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('pointerover', this.#handleLinkedProductColorPointerOver);
    this.addEventListener('pointerout', this.#handleLinkedProductColorPointerOut);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('pointerover', this.#handleLinkedProductColorPointerOver);
    this.removeEventListener('pointerout', this.#handleLinkedProductColorPointerOut);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId, variantId, connectedProductUrl } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Empty string removes ?variant=.
    if (typeof variantId === 'string') {
      this.applyVariantToLinks(variantId, typeof connectedProductUrl === 'string' ? connectedProductUrl : undefined);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html, typeof variantId === 'string' ? variantId : undefined);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   * @param {string} [intendedVariantId] - User-intended variant.
   */
  #updateProductUrl(html, intendedVariantId) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const responseUrl = new URL(anchorElement.href);
      this.#updateLinks({
        pathname: responseUrl.pathname,
        variantId: intendedVariantId ?? responseUrl.searchParams.get('variant'),
      });
    }
  }

  /**
   * Applies the given variant ID to the variant param of every link in the card.
   * Public so the components, namely swatches component can call it.
   * @param {string | null} variantId - The variant ID to set, or null to remove it.
   * @param {string} [productUrl] - Product URL path to adopt.
   */
  applyVariantToLinks(variantId, productUrl) {
    let pathname;
    if (productUrl) {
      try {
        pathname = new URL(productUrl, window.location.origin).pathname;
      } catch (error) {
        console.warn('[product-card] Invalid product URL:', productUrl, error);
        return;
      }
    }

    this.#updateLinks({ pathname, variantId });
  }

  /**
   * Updates every link in the card (overlay, gallery, and title) in place, preserving
   * each link's existing query params so tracking params stay in sync.
   * Always sets (or removes) the `variant` param; optionally adopts a new pathname (for combined-listing child navigation).
   * @param {object} options
   * @param {string} [options.pathname] - New pathname to adopt (combined-listing child navigation). Omit to keep each link's current path.
   * @param {string | null} [options.variantId] - Variant ID to set, or null/undefined to remove it.
   */
  #updateLinks({ pathname, variantId }) {
    const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;
    for (const linkEl of [productCardLink, cardGalleryLink, productTitleLink]) {
      if (!(linkEl instanceof HTMLAnchorElement) || !linkEl.href) continue;
      const url = new URL(linkEl.href);
      if (pathname) url.pathname = pathname;
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      linkEl.href = url.toString();
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }

    const progress = this.querySelector('.card-gallery__progress');
    if (progress instanceof HTMLElement) {
      const count = Number(progress.style.getPropertyValue('--slide-count')) || 1;
      const visibleSlides = this.refs.slideshow?.slides?.filter((slide) => !slide.hasAttribute('hidden')) ?? [];
      const position = visibleSlides.indexOf(/** @type {HTMLElement} */ (event.detail.slide));
      const index = position >= 0 ? position : event.detail.index;
      progress.style.setProperty('--slide-index', String(((index % count) + count) % count));
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /** @type {{ img: HTMLImageElement; src: string; srcset: string; alt: string } | null} */
  #linkedProductImagePreview = null;

  /**
   * Previews a linked product's featured image from card color swatches.
   * @param {PointerEvent} event
   */
  previewLinkedProductImage(event) {
    if (event.pointerType !== 'mouse' || !(event.target instanceof HTMLElement)) return;

    const swatchLink = event.target.closest('[data-preview-image-url]');
    if (!(swatchLink instanceof HTMLElement)) return;

    this.#previewLinkedProductImage(swatchLink);
  }

  /**
   * Handles delegated hover preview for linked product color swatches.
   * @param {PointerEvent} event
   */
  #handleLinkedProductColorPointerOver = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof HTMLElement)) return;

    const swatchLink = event.target.closest('.product-card-linked-colors__link[data-preview-image-url]');
    if (!(swatchLink instanceof HTMLElement)) return;

    this.#previewLinkedProductImage(swatchLink);
  };

  /**
   * Handles delegated hover reset for linked product color swatches.
   * @param {PointerEvent} event
   */
  #handleLinkedProductColorPointerOut = (event) => {
    if (event.pointerType !== 'mouse' || !(event.target instanceof HTMLElement)) return;

    const swatchLink = event.target.closest('.product-card-linked-colors__link[data-preview-image-url]');
    if (!(swatchLink instanceof HTMLElement) || swatchLink.contains(/** @type {Node | null} */ (event.relatedTarget))) {
      return;
    }

    this.#resetLinkedProductImage();
  };

  /**
   * Previews a linked product's featured image.
   * @param {HTMLElement} swatchLink
   */
  #previewLinkedProductImage(swatchLink) {
    const previewImageUrl = swatchLink.dataset.previewImageUrl;
    if (!previewImageUrl) return;

    const activeImage = this.#getActiveCardImage();
    if (!activeImage) return;

    if (!this.#linkedProductImagePreview || this.#linkedProductImagePreview.img !== activeImage) {
      this.#linkedProductImagePreview = {
        img: activeImage,
        src: activeImage.src,
        srcset: activeImage.srcset,
        alt: activeImage.alt,
      };
    }

    activeImage.srcset = previewImageUrl;
    activeImage.src = previewImageUrl;
    activeImage.alt = swatchLink.dataset.previewImageAlt || activeImage.alt;
  }

  /**
   * Restores the card image after previewing a linked product color.
   * @param {PointerEvent} event
   */
  resetLinkedProductImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.#resetLinkedProductImage();
  }

  /**
   * Restores the card image after previewing a linked product color.
   */
  #resetLinkedProductImage() {

    const preview = this.#linkedProductImagePreview;
    if (!preview) return;

    preview.img.srcset = preview.srcset;
    preview.img.src = preview.src;
    preview.img.alt = preview.alt;
    this.#linkedProductImagePreview = null;
  }

  /**
   * Switches the card to a linked color product instead of navigating to it.
   * The card is replaced by an updated clone so apps that decorate cards (e.g. wishlist) pick up the new product.
   * @param {MouseEvent} event
   */
  selectLinkedProduct(event) {
    if (!(event.target instanceof Element)) return;

    const swatchLink = event.target.closest('[data-linked-product-url]');
    if (!(swatchLink instanceof HTMLAnchorElement)) return;
    if (Number(swatchLink.dataset.linkedVariantCount) > 1) return;

    event.preventDefault();
    if (swatchLink.classList.contains('product-card-linked-colors__link--current')) return;

    const data = swatchLink.dataset;
    const productId = data.linkedProductId || '';
    const title = data.linkedProductTitle || '';
    const available = data.linkedAvailable === 'true';
    const images = (data.linkedImages || '').split('|').filter(Boolean);
    const currentPath = new URL(this.refs.productCardLink.href, window.location.origin).pathname;
    const swatchIndex = [...this.querySelectorAll('.product-card-linked-colors__link')].indexOf(swatchLink);

    this.#resetLinkedProductImage();
    this.#registerLinkedProductWithWishlist(data);

    const card = /** @type {HTMLElement} */ (this.cloneNode(true));
    card.dataset.productId = productId;
    card.closest('li')?.setAttribute('data-product-id', productId);
    card.querySelectorAll('[class*="Vtl-"], .spikes-wishlist-proxy').forEach((element) => {
      if (element.parentElement?.closest('[class*="Vtl-"], .spikes-wishlist-proxy')) return;
      element.remove();
    });

    card.querySelectorAll('a[href]').forEach((link) => {
      if (link.closest('.product-card-linked-colors')) return;
      const url = new URL(link.getAttribute('href') || '', window.location.origin);
      if (url.pathname === currentPath) link.setAttribute('href', data.linkedProductUrl || '');
    });

    card.querySelectorAll('[ref="productTitleLink"] [role="heading"], .product-grid-view-zoom-out--details h3').forEach((heading) => {
      heading.textContent = title;
    });

    card.querySelectorAll('product-price, .card-gallery, quick-add-component, product-form-component').forEach((element) => {
      element.setAttribute('data-product-id', productId);
    });
    card.querySelector('quick-add-component')?.setAttribute('data-product-title', title);

    const price = card.querySelector('product-price');
    if (price && data.linkedPrice) price.innerHTML = data.linkedPrice;

    const gallery = card.querySelector('.card-gallery');
    if (gallery) {
      gallery.querySelector('.product-badges')?.remove();
      if (data.linkedBadges) gallery.insertAdjacentHTML('afterbegin', data.linkedBadges);
      const hasBadges = Boolean(gallery.querySelector('.product-badges'));
      [...gallery.classList].forEach((name) => {
        if (name.startsWith('card-gallery--badge-') && !hasBadges) gallery.classList.remove(name);
      });
    }

    this.#applyLinkedImages(card, images, title, productId);

    const variantInput = card.querySelector('product-form-component input[name="id"]');
    if (variantInput instanceof HTMLInputElement && data.linkedVariantId) {
      variantInput.value = data.linkedVariantId;
      variantInput.disabled = !available;
    }
    const addButton = card.querySelector('.quick-add__button--add');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = !available;

    card.querySelectorAll('.product-card-linked-colors__link').forEach((link, index) => {
      link.classList.toggle('product-card-linked-colors__link--current', index === swatchIndex);
    });

    this.replaceWith(card);
    setTimeout(() => ensureWishlistHeart(card, productId), 2500);
  }

  /**
   * Makes the wishlist app aware of a linked product that is not rendered on the page,
   * so it can place its heart on the switched card.
   * @param {DOMStringMap} data
   */
  #registerLinkedProductWithWishlist(data) {
    const vitalsData = /** @type {any} */ (window).vtlsLiquidData;
    const products = vitalsData?.collectionProducts;
    if (!products || !data.linkedHandle || products[data.linkedHandle]) return;

    products[data.linkedHandle] = {
      id: Number(data.linkedProductId),
      variantId: Number(data.linkedVariantId),
      collectionIds: (data.linkedCollectionIds || '').split(',').filter(Boolean).map(Number),
    };
  }

  /**
   * Rebuilds the card gallery of a cloned card with the images of a linked product.
   * @param {HTMLElement} card
   * @param {string[]} images
   * @param {string} alt
   * @param {string} productId
   */
  #applyLinkedImages(card, images, alt, productId) {
    if (!images.length) return;

    const setImage = (/** @type {HTMLImageElement} */ image, /** @type {string} */ source) => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
      image.alt = alt;
      image.loading = 'eager';
    };

    const slideshow = card.querySelector('.card-gallery slideshow-component');
    const slides = [...card.querySelectorAll('.card-gallery slideshow-slide')];

    if (!slideshow || !slides.length) {
      const image = card.querySelector('.card-gallery img');
      if (image instanceof HTMLImageElement) setImage(image, images[0]);
      return;
    }

    const container = slides[0].parentElement;
    const template = slides[0];
    slides.forEach((slide) => slide.remove());

    images.forEach((source, index) => {
      const slide = /** @type {HTMLElement} */ (template.cloneNode(true));
      slide.removeAttribute('hidden');
      slide.removeAttribute('variant-image');
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.setAttribute('slide-id', `${productId}-${index}`);
      slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
      const image = slide.querySelector('img');
      if (image) setImage(image, source);
      container?.append(slide);
    });

    slideshow.setAttribute('initial-slide', '0');
    slideshow.style.setProperty('--slideshow-timeline', images.map((_, index) => `--slide-${index}`).join(','));
    card.querySelectorAll('.card-gallery slideshow-arrows').forEach((arrows) => {
      if (images.length < 2) arrows.remove();
    });

    const progress = card.querySelector('.card-gallery__progress');
    if (progress instanceof HTMLElement) {
      if (images.length < 2) {
        progress.remove();
      } else {
        progress.style.setProperty('--slide-count', String(images.length));
        progress.style.setProperty('--slide-index', '0');
      }
    } else if (images.length > 1) {
      card.querySelector('.card-gallery')?.insertAdjacentHTML(
        'beforeend',
        `<span class="card-gallery__progress" style="--slide-count: ${images.length}; --slide-index: 0;" aria-hidden="true"><span class="card-gallery__progress-thumb"></span></span>`
      );
    }
  }

  /**
   * Gets the currently visible product-card image.
   * @returns {HTMLImageElement | null}
   */
  #getActiveCardImage() {
    const { cardGallery, slideshow } = this.refs;
    const activeSlide = slideshow?.slides?.find((slide) => slide.getAttribute('aria-hidden') === 'false');
    return activeSlide?.querySelector('img') || cardGallery?.querySelector('img') || null;
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Handles card swatch changes.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const hasAvailableVariant = clickedSwatch.dataset.hasAvailableVariant === 'true';
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Request the first available variant for this swatch.
    if (isSwatchInput && hasAvailableVariant && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const optionValueId = clickedSwatch.dataset.optionValueId || '';
      const connectedProductUrl = clickedSwatch.dataset.connectedProductUrl || '';
      const requestUrl = this.buildRequestUrl(clickedSwatch, 'product-card', [optionValueId]);

      this.fetchUpdatedSection(requestUrl, {
        detail: { optionValueId, variantId: firstAvailableVariantId, connectedProductUrl },
      });
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
